// src/components/common/PayPalCheckoutButton/PayPalCheckoutButton.tsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ENV } from '../../../config/env';
import { http } from '../../../api/http';

declare global {
  interface Window {
    paypal?: any;
  }
}

interface PayPalCheckoutButtonProps {
  amount: number;
  description: string;
  invoiceId: string;
  currency: 'MXN' | 'USD';
  onSuccess?: (data: { orderID: string }) => void | Promise<void>;
  onError?: (message: string) => void;
  disabled?: boolean;
}

function loadPayPalSdk(clientId: string, currency: string) {
  return new Promise<void>((resolve, reject) => {
    const existing = document.getElementById('paypal-sdk');
    if (existing) {
      if (window.paypal) return resolve();
      if ((existing as HTMLScriptElement).dataset.loaded === 'true') return resolve();

      let settled = false;
      let timeoutId: number | undefined;
      const onLoad = () => {
        if (settled) return;
        settled = true;
        if (timeoutId) window.clearTimeout(timeoutId);
        (existing as HTMLScriptElement).dataset.loaded = 'true';
        resolve();
      };
      const onError = () => {
        if (settled) return;
        settled = true;
        if (timeoutId) window.clearTimeout(timeoutId);
        reject(new Error('PAYPAL_SDK_LOAD_FAILED'));
      };

      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', onError, { once: true });

      // Fallback: si el script ya cargó pero el evento no se dispara
      timeoutId = window.setTimeout(() => {
        if (settled) return;
        if (window.paypal) {
          settled = true;
          (existing as HTMLScriptElement).dataset.loaded = 'true';
          resolve();
        } else {
          settled = true;
          reject(new Error('PAYPAL_SDK_NOT_AVAILABLE'));
        }
      }, 8000);
      return;
    }

    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
      clientId
    )}&currency=${encodeURIComponent(currency)}`;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error('PAYPAL_SDK_LOAD_FAILED'));
    document.body.appendChild(script);
  });
}

export default function PayPalCheckoutButton({
  amount,
  description,
  invoiceId,
  currency,
  onSuccess,
  onError,
  disabled
}: PayPalCheckoutButtonProps) {
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  const hasRenderedRef = useRef(false);
  const paypalButtonsRef = useRef<any>(null);

  const clientId = useMemo(() => ENV.PAYPAL_CLIENT_ID || '', []);
  const sdkCurrency = useMemo(() => ENV.PAYPAL_CURRENCY || currency, [currency]);
  const buttonContainerId = useMemo(() => `pp-${invoiceId}`, [invoiceId]);

  // Cleanup function
  const cleanupPayPalButton = () => {
    if (paypalButtonsRef.current) {
      try {
        paypalButtonsRef.current.close();
      } catch (e) {
        // Ignore cleanup errors
      }
      paypalButtonsRef.current = null;
    }
    hasRenderedRef.current = false;
    setReady(false);
  };

  // Effect para limpiar cuando cambia el ID del contenedor
  useEffect(() => {
    cleanupPayPalButton();
    setErr(null);
  }, [buttonContainerId]);

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      cleanupPayPalButton();
    };
  }, []);

  // Effect para cargar el SDK de PayPal
  useEffect(() => {
    if (disabled) return;

    if (!clientId || clientId === 'YOUR_PAYPAL_CLIENT_ID' || clientId.trim() === '') {
      setErr('PAYPAL_CLIENT_ID_NOT_CONFIGURED');
      return;
    }

    loadPayPalSdk(clientId, sdkCurrency)
      .then(() => {
        if (!window.paypal) throw new Error('PAYPAL_SDK_NOT_AVAILABLE');
        setSdkLoaded(true);
      })
      .catch((e: any) => {
        const message = typeof e?.message === 'string' ? e.message : 'PAYPAL_INIT_FAILED';
        setErr(message);
        onError?.(message);
      });
  }, [clientId, sdkCurrency, disabled]);

  // Effect para renderizar el botón cuando el SDK esté cargado y el DOM esté listo
  useLayoutEffect(() => {
    if (!sdkLoaded || disabled || hasRenderedRef.current || err) return;

    // Usar un pequeño timeout para asegurar que el DOM esté completamente listo
    const timeoutId = setTimeout(() => {
      // Verificar que el elemento existe
      const element = document.getElementById(buttonContainerId);
      if (!element) {
        console.error(`PayPal container element #${buttonContainerId} not found`);
        setErr('PAYPAL_CONTAINER_NOT_FOUND');
        return;
      }

      try {
        cleanupPayPalButton(); // Limpiar cualquier botón existente

        paypalButtonsRef.current = window.paypal.Buttons({
          createOrder: async () => {
            try {
              const res = await http<{ data: { paypalOrderId: string } }>('/payments/paypal/orders', {
                method: 'POST',
                body: { 
                  amount: amount.toFixed(2), 
                  description, 
                  invoiceId, 
                  currency: sdkCurrency 
                }
              });
              
              if (!res || !res.data || !res.data.paypalOrderId) {
                throw new Error('Invalid response from create-order');
              }
              
              return res.data.paypalOrderId;
            } catch (error) {
              console.error('Error creating PayPal order:', error);
              const message = error instanceof Error ? error.message : 'Error creating order';
              setErr(message);
              onError?.(message);
              throw error;
            }
          },
          onApprove: async (data: any) => {
            try {
              if (!data?.orderID) {
                throw new Error('Order ID not provided');
              }
              
              console.log('Capturing PayPal order:', data.orderID);
              const response = await http<{ success: boolean; data: any }>('/payments/paypal/orders/capture', {
                method: 'POST',
                body: { paypalOrderId: data.orderID, invoiceId }
              });
              
              console.log('Capture response:', response);
              
              if (!response?.success) {
                throw new Error('Capture failed');
              }
              
              await onSuccess?.({ orderID: data.orderID });
            } catch (error) {
              console.error('Error capturing PayPal order:', error);
              const message = error instanceof Error ? error.message : 'Error processing payment';
              setErr(message);
              onError?.(message);
            }
          },
          onError: (e: any) => {
            const message = typeof e?.message === 'string' ? e.message : 'PAYPAL_ERROR';
            setErr(message);
            onError?.(message);
          }
        });

        paypalButtonsRef.current.render(`#${buttonContainerId}`);
        hasRenderedRef.current = true;
        setReady(true);
      } catch (error) {
        console.error('Error rendering PayPal button:', error);
        const message = error instanceof Error ? error.message : 'PAYPAL_RENDER_FAILED';
        setErr(message);
        onError?.(message);
      }
    }, 100); // Pequeño delay para asegurar que el DOM esté listo

    return () => clearTimeout(timeoutId);
  }, [sdkLoaded, disabled, amount, description, invoiceId, sdkCurrency, buttonContainerId, onSuccess, onError]);

  const getErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case 'PAYPAL_CLIENT_ID_NOT_CONFIGURED':
        return 'PayPal no está configurado correctamente. Contacta al administrador.';
      case 'PAYPAL_SDK_LOAD_FAILED':
        return 'Error al cargar PayPal SDK. Verifica tu conexión a internet.';
      case 'PAYPAL_SDK_NOT_AVAILABLE':
        return 'PayPal SDK no disponible. Intenta recargar la página.';
      case 'PAYPAL_CONTAINER_NOT_FOUND':
        return 'Error interno: contenedor PayPal no encontrado. Intenta recargar la página.';
      case 'PAYPAL_RENDER_FAILED':
        return 'Error al renderizar botón PayPal. Intenta recargar la página.';
      default:
        return errorCode;
    }
  };

  // Renderizado condicional basado en estados
  if (disabled) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
        PayPal button disabled
      </div>
    );
  }
  
  if (err) {
    return (
      <div style={{ 
        padding: '1rem', 
        textAlign: 'center', 
        backgroundColor: '#fff3cd', 
        border: '1px solid #ffeaa7',
        borderRadius: '4px',
        color: '#856404'
      }}>
        <strong>Error de PayPal:</strong> {getErrorMessage(err)}
        <br />
        {err !== 'PAYPAL_CLIENT_ID_NOT_CONFIGURED' && (
          <button 
            onClick={() => {
              setErr(null);
              setReady(false);
              hasRenderedRef.current = false;
            }}
            style={{
              marginTop: '0.5rem',
              padding: '0.25rem 0.5rem',
              fontSize: '0.875rem',
              backgroundColor: '#ffc107',
              color: '#212529',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Reintentar
          </button>
        )}
      </div>
    );
  }
  
  // SIEMPRE renderizar el contenedor para que esté disponible en el DOM
  return (
    <div>
      {!ready && (
        <div style={{ padding: '1rem', textAlign: 'center' }}>
          Cargando PayPal...
        </div>
      )}
      <div 
        id={buttonContainerId} 
        style={{ 
          minHeight: ready ? 'auto' : '45px',
          visibility: ready ? 'visible' : 'hidden' 
        }} 
      />
    </div>
  );
}
